import React, { useMemo } from 'react';
import CrossTeamGanttView from '../teams/CrossTeamGanttView';

// The four gag milestone dates (Task 3.3). Order = lane render order; shape
// picks the marker colour/glyph in CrossTeamGanttView's milestone lane.
// SINGLE SOURCE for the milestone field defs — GagDetailPanel imports this
// (never redefine the four fields elsewhere).
export const MILESTONE_DEFS = [
  { field: 'shoot_date', label: 'Shoot', shape: 'diamond' },
  { field: 'location_access_date', label: 'Access', shape: 'triangle' },
  { field: 'prep_date', label: 'Prep', shape: 'circle' },
  { field: 'strike_date', label: 'Strike', shape: 'square' },
];

/**
 * Thin Gantt wrapper for ONE gag's child tasks (Task 3.2) plus the gag's
 * milestone lane (Task 3.3).
 *
 * Reuses CrossTeamGanttView by mapping gag tasks onto its expected task
 * shape: we pin every task to a synthetic team (id 0) named after the gag so
 * the chart renders exactly one collapsible group — but each task KEEPS its
 * own team_color (from the API's LEFT JOIN teams), so a task attached to a
 * team paints in that team's colour and a team-less gag task falls back to a
 * status colour (statusColorFallback). Pin team_id AFTER the spread so the
 * grouping stays one-per-gag while the real team colour still drives the bar.
 * The gag's four dates (shoot / location access / prep / strike) become
 * draggable markers in a dedicated milestone lane; null dates are skipped and
 * all markers share the gag-level can_edit gate (canDrag).
 *
 * Props:
 *  - gag: the gag detail (title used as the group label; dates feed the lane)
 *  - tasks: the gag's child tasks
 *  - canDrag: gag-level can_edit (drag-to-reschedule + milestone drag)
 *  - onTaskClick(task): open the task editor
 *  - onDragComplete(task, newStart, newDue): persist a reschedule
 *  - onMilestoneDrag(field, newDate): persist a milestone date move
 */
export default function GagGanttView({ gag, tasks, canDrag, onTaskClick, onDragComplete, onMilestoneDrag }) {
  // Keep each task's real team_color (server LEFT JOIN teams) — only override
  // team_id/team_name so the whole gag renders as one group. team_color null
  // (team-less task) → status-coloured bar via statusColorFallback below.
  const mapped = useMemo(
    () => tasks.map((t) => ({ ...t, team_id: 0, team_name: gag.title })),
    [tasks, gag.title]
  );

  const milestones = useMemo(
    () =>
      MILESTONE_DEFS.filter((def) => gag[def.field]).map((def) => ({
        ...def,
        date: String(gag[def.field]).slice(0, 10),
        draggable: !!canDrag,
      })),
    [gag, canDrag]
  );

  const hasDated = mapped.some((t) => t.start_date || t.due_date);
  // Only fully empty (no dated tasks AND no milestone dates) collapses to the
  // text placeholder — a milestone-only gag still gets its axis + lane.
  if (!hasDated && milestones.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400 italic">
        No tasks with dates yet — add start/due dates to tasks to see them on the timeline.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {!hasDated && (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
          No tasks with dates yet — add start/due dates to tasks to see them on the timeline.
        </p>
      )}
      <CrossTeamGanttView
        tasks={mapped}
        onTaskClick={onTaskClick}
        canDrag={canDrag}
        onDragComplete={onDragComplete}
        milestones={milestones}
        onMilestoneDrag={onMilestoneDrag}
        showAssignees
        showSummary
        statusColorFallback
      />
    </div>
  );
}
